import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Plus, RefreshCw, ExternalLink, Trash2, Pencil, Sparkles, Car,
  TrendingDown, TrendingUp, AlertCircle, ShieldCheck, Search, Handshake, Crown, Send, Info,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

const EMPTY = {
  url: "", title: "", image_url: "", price: "",
  year: "", make: "", model: "", trim: "", mileage: "", notes: "",
};

const TIER_STYLES = {
  "Great Deal":     { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  "Good Deal":      { cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  "Fair Price":     { cls: "bg-blue-500/10 text-blue-300 border-blue-500/25" },
  "Above Market":   { cls: "bg-red-500/10 text-red-300 border-red-500/30" },
  "Not Enough Data":{ cls: "bg-white/5 text-gray-400 border-white/10" },
};

const CONFIDENCE_LABEL = {
  high: { label: "High", cls: "text-emerald-300" },
  medium: { label: "Medium", cls: "text-blue-300" },
  low: { label: "Low", cls: "text-yellow-300" },
  none: { label: "—", cls: "text-gray-500" },
};

const TIER_METHODOLOGY = {
  "Great Deal":      "Asking price is at least 10% below the market median.",
  "Good Deal":       "Asking price is 3–10% below the market median.",
  "Fair Price":      "Asking price sits within ±7% of the market median.",
  "Above Market":    "Asking price is more than 7% above the market median.",
  "Not Enough Data": "We couldn't find enough comparable listings to make an honest call. We'd rather show this than guess.",
};

function DealRatingBadge({ rating }) {
  if (!rating) return null;
  const style = TIER_STYLES[rating.tier] || TIER_STYLES["Not Enough Data"];
  const pct = rating.delta_pct != null ? `${(rating.delta_pct * 100).toFixed(1)}%` : null;
  const conf = CONFIDENCE_LABEL[rating.confidence] || CONFIDENCE_LABEL.none;
  const basisText = rating.basis === "trim"
    ? "Same year, make, model, and trim"
    : rating.basis === "make_model"
    ? "Same year, make, and model (trim comps not available)"
    : "—";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`text-[10px] px-2 py-1 rounded border font-medium inline-flex items-center gap-1.5 hover:opacity-90 ${style.cls}`}
          data-testid="garage-deal-rating"
        >
          <span>{rating.tier}</span>
          {pct && rating.tier !== "Not Enough Data" && (
            <span className="opacity-70 font-mono">· {rating.delta_pct < 0 ? "" : "+"}{pct}</span>
          )}
          <Info className="w-3 h-3 opacity-70" data-testid="garage-deal-rating-info" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="bg-[#0A0A0E] border-[#1A1A24] text-white w-80 p-0" align="start">
        <div className="p-4 border-b border-white/5">
          <div className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded border ${style.cls} mb-2`}>
            {rating.tier}
            {pct && rating.tier !== "Not Enough Data" && (
              <span className="opacity-70 font-mono text-[11px]">· {rating.delta_pct < 0 ? "" : "+"}{pct}</span>
            )}
          </div>
          <p className="text-xs text-gray-400 leading-relaxed">
            {TIER_METHODOLOGY[rating.tier]}
          </p>
        </div>

        {rating.tier !== "Not Enough Data" && (
          <div className="p-4 border-b border-white/5 space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Market median</span>
              <span className="font-mono">${(rating.median_price || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Your listing&apos;s delta</span>
              <span className={`font-mono ${rating.delta_pct < 0 ? "text-emerald-300" : "text-red-300"}`}>
                {rating.delta_pct != null ? `${rating.delta_pct > 0 ? "+" : ""}${(rating.delta_pct * 100).toFixed(1)}%` : "—"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Comparable listings</span>
              <span className="font-mono">{rating.comparables_count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Confidence</span>
              <span className={`font-mono ${conf.cls}`}>{conf.label}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Match basis</span>
              <span className="text-gray-300 text-[11px] text-right max-w-[60%]">{basisText}</span>
            </div>
          </div>
        )}

        <div className="p-4 text-[11px] text-gray-500 leading-relaxed">
          <div className="uppercase tracking-wider text-[9px] text-gray-600 mb-1.5">How we calculate this</div>
          Comparables are pulled from the authorized dealer inventory (same year, make, model, trim). We need at least 3 comparable listings to make a call — anything less becomes &quot;Not Enough Data.&quot; When trim comps are sparse, we fall back to make + model and downgrade confidence.
          {rating.tier === "Not Enough Data" && (
            <div className="mt-3 pt-3 border-t border-white/5 text-gray-400">
              <b className="text-gray-300">This will improve automatically</b> once real dealer feeds come online — the same comparison logic just gets a much bigger pool to work with.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function hostBadge(host) {
  const map = {
    "cars.com": "Cars.com",
    "autotrader.com": "Autotrader",
    "cargurus.com": "CarGurus",
    "truecar.com": "TrueCar",
    "autotempest.com": "AutoTempest",
    "facebook.com": "Facebook",
    "carmax.com": "CarMax",
    "carvana.com": "Carvana",
  };
  return map[host] || host || "Link";
}

function priceDelta(current, first) {
  if (!current || !first || current === first) return null;
  const pct = ((current - first) / first) * 100;
  return { pct, up: pct > 0 };
}

const ACTIONS = [
  { key: "analysis", label: "Request Analysis", short: "Analyze", icon: Search, desc: "A broker reviews this listing and sends an honest assessment: fair price, red flags, and comparables." },
  { key: "negotiation", label: "Request Negotiation", short: "Negotiate", icon: Handshake, desc: "A broker drafts your negotiation strategy — or handles the dealer conversation for you." },
  { key: "concierge", label: "Start Concierge", short: "Concierge", icon: Crown, desc: "Hands-off. A broker takes over: pricing, financing, paperwork, delivery." },
];

function GarageCard({ item, onRefresh, onDelete, onEdit, onRequestAction }) {
  const [busy, setBusy] = useState(false);
  const doRefresh = async () => {
    setBusy(true);
    try { await onRefresh(item.id); }
    finally { setBusy(false); }
  };
  const delta = priceDelta(item.price, item.price_first);

  return (
    <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden group" data-testid={`garage-card-${item.id}`}>
      <div className="aspect-[16/10] bg-[#050507] relative overflow-hidden">
        {item.image_url ? (
          <img src={item.image_url} alt={item.title} loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-600">
            <Car className="w-10 h-10 mb-2" />
            <div className="text-xs">No image saved</div>
          </div>
        )}
        <div className="absolute top-3 left-3">
          <span className="text-[10px] px-2 py-0.5 rounded bg-black/70 border border-white/10 backdrop-blur font-medium">
            {hostBadge(item.host)}
          </span>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <div className="font-display text-base leading-tight truncate">
              {item.title || `${item.year || ""} ${item.make || ""} ${item.model || ""}`.trim() || "Untitled"}
            </div>
            {item.trim && <div className="text-xs text-gray-500 mt-0.5">{item.trim}</div>}
          </div>
          <div className="text-right shrink-0">
            <div className="font-display text-lg">{item.price ? `$${Number(item.price).toLocaleString()}` : "—"}</div>
            {delta && (
              <div className={`text-[10px] font-mono inline-flex items-center gap-0.5 ${delta.up ? "text-red-400" : "text-emerald-400"}`}>
                {delta.up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {delta.up ? "+" : ""}{delta.pct.toFixed(1)}%
              </div>
            )}
          </div>
        </div>

        {item.notes && (
          <p className="text-xs text-gray-400 mb-3 line-clamp-2 italic">&ldquo;{item.notes}&rdquo;</p>
        )}

        {item.deal_rating && (
          <div className="mb-3">
            <DealRatingBadge rating={item.deal_rating} />
          </div>
        )}

        <div className="text-[10px] text-gray-500 flex items-center gap-2 mb-3">
          <span>Added {new Date(item.added_at).toLocaleDateString()}</span>
          {item.last_refreshed_at && <span>· Refreshed {new Date(item.last_refreshed_at).toLocaleDateString()}</span>}
        </div>

        {/* Broker actions */}
        <div className="grid grid-cols-3 gap-1.5 mb-3">
          {ACTIONS.map((a) => (
            <button
              key={a.key}
              onClick={() => onRequestAction(item, a.key)}
              className="text-[10px] px-2 py-1.5 rounded-lg border border-emerald-500/20 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-500/40 transition-colors inline-flex items-center justify-center gap-1"
              data-testid={`garage-action-${a.key}-${item.id}`}
              title={a.desc}
            >
              <a.icon className="w-3 h-3" /> {a.short}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5 pt-3 border-t border-white/5">
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 text-xs px-3 py-1.5 rounded-full silver-gradient text-[#050507] font-bold inline-flex items-center justify-center gap-1"
            data-testid={`garage-open-${item.id}`}
          >
            <ExternalLink className="w-3 h-3" /> Open
          </a>
          <button
            onClick={doRefresh}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5 inline-flex items-center gap-1 disabled:opacity-40"
            data-testid={`garage-refresh-${item.id}`}
            title="Re-check price and image"
          >
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => onEdit(item)}
            className="text-xs px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5"
            data-testid={`garage-edit-${item.id}`}
            title="Edit"
          >
            <Pencil className="w-3 h-3" />
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="text-xs px-3 py-1.5 rounded-full border border-red-500/20 text-red-400 hover:bg-red-500/10"
            data-testid={`garage-delete-${item.id}`}
            title="Remove"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionDialog({ open, onOpenChange, item, actionKey, onSent }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setMessage(""); }, [open]);
  if (!item || !actionKey) return null;
  const action = ACTIONS.find((a) => a.key === actionKey);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/garage/${item.id}/request-action`, { action_type: actionKey, message });
      toast.success("Broker notified — you&apos;ll get a confirmation email");
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0E] border-[#1A1A24] text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2"><action.icon className="w-4 h-4 text-emerald-400" /> {action.label}</DialogTitle>
          <DialogDescription className="text-gray-500 text-sm">{action.desc}</DialogDescription>
        </DialogHeader>
        <div className="p-3 rounded-lg bg-[#050507] border border-white/5 my-2">
          <div className="text-sm font-medium truncate">{item.title || `${item.year || ""} ${item.make || ""} ${item.model || ""}`}</div>
          <div className="text-xs text-gray-500 mt-0.5">{item.host} · {item.price ? `$${Number(item.price).toLocaleString()}` : "no price saved"}</div>
        </div>
        <div>
          <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Message to broker (optional)</div>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            placeholder="e.g. Really only in white/tan. Willing to fly to pick it up."
            className="bg-[#050507] border-[#1A1A24] text-white"
            data-testid="garage-action-message"
          />
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-full border border-white/15 text-sm">Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-6 py-2 rounded-full silver-gradient text-[#050507] font-bold text-sm inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="garage-action-submit"
          >
            <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send Request"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SendGarageDialog({ open, onOpenChange, count, onSent }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setMessage(""); }, [open]);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/garage/send-to-broker", { message });
      toast.success(`Handoff sent — a broker will review all ${data.items} vehicle${data.items !== 1 ? "s" : ""} shortly`);
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0E] border-[#1A1A24] text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2"><Send className="w-4 h-4 text-emerald-400" /> Send garage to broker</DialogTitle>
          <DialogDescription className="text-gray-500 text-sm">
            A 555 broker will review all <b className="text-white">{count} vehicle{count !== 1 ? "s" : ""}</b> in your garage and reach out — usually within one business day. You&apos;ll get a confirmation email.
          </DialogDescription>
        </DialogHeader>
        <div>
          <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Anything the broker should know? (optional)</div>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            placeholder="e.g. Budget max $700/mo · need before end of quarter · white/tan preferred"
            className="bg-[#050507] border-[#1A1A24] text-white"
            data-testid="garage-send-message"
          />
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-full border border-white/15 text-sm">Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-6 py-2 rounded-full silver-gradient text-[#050507] font-bold text-sm inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="garage-send-submit"
          >
            <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send to Broker"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddDialog({ open, setOpen, onSaved, editing }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(editing ? { ...EMPTY, ...editing, price: editing.price ?? "" } : EMPTY);
  }, [open, editing]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const fetchPreview = async () => {
    if (!form.url) { toast.error("Paste a URL first"); return; }
    setPreviewBusy(true);
    try {
      const { data } = await api.post("/garage/link-preview", { url: form.url });
      setForm((s) => ({
        ...s,
        title: s.title || data.title || "",
        image_url: s.image_url || data.image_url || "",
        price: s.price || (data.price ?? ""),
      }));
      if (data.title || data.image_url || data.price) {
        toast.success("Preview filled — edit any field you want");
      } else {
        toast("No preview available for this site — fill in manually.", { icon: "ℹ️" });
      }
    } catch {
      toast.error("Preview failed — you can still fill fields manually");
    } finally {
      setPreviewBusy(false);
    }
  };

  const save = async () => {
    if (!form.url) { toast.error("URL is required"); return; }
    setBusy(true);
    try {
      const body = {
        ...form,
        price: form.price === "" ? null : Number(form.price),
        year: form.year === "" ? null : Number(form.year),
        mileage: form.mileage === "" ? null : Number(form.mileage),
      };
      if (editing) await api.patch(`/garage/${editing.id}`, body);
      else await api.post("/garage", body);
      toast.success(editing ? "Updated" : "Saved to your garage");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#0A0A0E] border-[#1A1A24] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit vehicle" : "Add to your garage"}</DialogTitle>
          <DialogDescription className="text-gray-500 text-sm">
            Paste any dealer or marketplace URL. We&apos;ll pull the public link preview so you can save it — or fill fields manually.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Vehicle URL *</div>
            <div className="flex gap-2">
              <Input
                value={form.url}
                onChange={(e) => set("url", e.target.value)}
                placeholder="https://www.cars.com/vehicledetail/…"
                className="bg-[#050507] border-[#1A1A24] text-white flex-1"
                disabled={!!editing}
                data-testid="garage-url-input"
              />
              {!editing && (
                <button
                  onClick={fetchPreview}
                  disabled={previewBusy || !form.url}
                  className="px-4 py-2 rounded-lg border border-white/15 text-xs hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-40 shrink-0"
                  data-testid="garage-fetch-preview-btn"
                >
                  <Sparkles className={`w-3.5 h-3.5 ${previewBusy ? "animate-spin" : ""}`} />
                  {previewBusy ? "Fetching…" : "Auto-fill"}
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Title</div>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. 2023 BMW X5 xDrive40i" className="bg-[#050507] border-[#1A1A24] text-white" data-testid="garage-title-input" />
            </div>
            <div>
              <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Asking Price ($)</div>
              <Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="58500" className="bg-[#050507] border-[#1A1A24] text-white" data-testid="garage-price-input" />
            </div>
          </div>

          <div className="grid grid-cols-5 gap-3">
            <div>
              <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Year</div>
              <Input type="number" value={form.year} onChange={(e) => set("year", e.target.value)} placeholder="2023" className="bg-[#050507] border-[#1A1A24] text-white" data-testid="garage-year-input" />
            </div>
            <div>
              <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Make</div>
              <Input value={form.make} onChange={(e) => set("make", e.target.value)} placeholder="BMW" className="bg-[#050507] border-[#1A1A24] text-white" data-testid="garage-make-input" />
            </div>
            <div>
              <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Model</div>
              <Input value={form.model} onChange={(e) => set("model", e.target.value)} placeholder="X5" className="bg-[#050507] border-[#1A1A24] text-white" data-testid="garage-model-input" />
            </div>
            <div>
              <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Trim</div>
              <Input value={form.trim} onChange={(e) => set("trim", e.target.value)} placeholder="xDrive40i" className="bg-[#050507] border-[#1A1A24] text-white" data-testid="garage-trim-input" />
            </div>
            <div>
              <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Mileage</div>
              <Input type="number" value={form.mileage} onChange={(e) => set("mileage", e.target.value)} placeholder="15000" className="bg-[#050507] border-[#1A1A24] text-white" data-testid="garage-mileage-input" />
            </div>
          </div>

          <div>
            <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Image URL</div>
            <Input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://…" className="bg-[#050507] border-[#1A1A24] text-white" data-testid="garage-image-input" />
          </div>

          <div>
            <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Notes</div>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="e.g. Great color combo · under budget" rows={2} className="bg-[#050507] border-[#1A1A24] text-white" data-testid="garage-notes-input" />
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full border border-white/15 text-sm">Cancel</button>
            <button
              onClick={save}
              disabled={busy}
              className="px-6 py-2 rounded-full silver-gradient text-[#050507] font-bold text-sm disabled:opacity-50"
              data-testid="garage-save-btn"
            >
              {busy ? "Saving…" : editing ? "Save changes" : "Save to Garage"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Garage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [actionItem, setActionItem] = useState(null);
  const [actionKey, setActionKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/garage");
      setItems(data);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error("Couldn't load your garage");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user]);

  const refresh = async (id) => {
    try {
      const { data } = await api.post(`/garage/${id}/refresh`);
      setItems((rows) => rows.map((r) => (r.id === id ? data : r)));
      toast.success("Refreshed");
    } catch { toast.error("Refresh failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this vehicle from your garage?")) return;
    try {
      await api.delete(`/garage/${id}`);
      setItems((rows) => rows.filter((r) => r.id !== id));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  const startEdit = (it) => { setEditing(it); setOpen(true); };
  const startCreate = () => { setEditing(null); setOpen(true); };
  const startAction = (it, key) => { setActionItem(it); setActionKey(key); };
  const closeAction = () => { setActionItem(null); setActionKey(null); };

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 md:py-16" data-testid="garage-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="label-mono mb-3">Your Garage</p>
          <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter">Your saved vehicles.</h1>
          <p className="text-gray-400 max-w-2xl mt-3">
            Paste any dealer or marketplace URL and we&apos;ll keep it here for you. Prices refresh when you open this page — no background tracking, no notifications you didn&apos;t ask for.
          </p>
        </div>
        <button
          onClick={startCreate}
          className="px-6 py-3 rounded-full silver-gradient text-[#050507] font-bold text-sm inline-flex items-center gap-2"
          data-testid="garage-add-btn"
        >
          <Plus className="w-4 h-4" /> Add Vehicle
        </button>
      </div>

      <div className="mb-8 p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
        <div className="text-sm text-emerald-100/90">
          <div className="font-medium mb-1">How this works</div>
          <div className="text-xs text-emerald-100/70 leading-relaxed">
            When you save a URL, we fetch the same public link preview (Open Graph tags) that any messaging app pulls to build a link card. No scraping, no background jobs — prices update only when you visit this page or hit the refresh button.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="aspect-[5/6] bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-white/10 rounded-2xl px-6" data-testid="garage-empty">
          <Car className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <div className="font-display text-2xl mb-3">Your garage is empty.</div>
          <p className="text-gray-400 max-w-lg mx-auto mb-7">Found a car on Cars.com, Autotrader, Facebook Marketplace, or anywhere else? Save it here to track price changes and hand it off to a broker later.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={startCreate} className="px-6 py-3 rounded-full silver-gradient text-[#050507] font-bold text-sm" data-testid="garage-empty-add-btn">
              Add Your First Vehicle
            </button>
            <Link to="/marketplace-search" className="px-6 py-3 rounded-full border border-white/15 text-sm hover:bg-white/5">
              Search Marketplaces
            </Link>
          </div>
        </div>
      ) : (
        <>
          <div className="text-sm text-gray-400 mb-5">{items.length} vehicle{items.length !== 1 ? "s" : ""} in your garage</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="garage-grid">
            {items.map((it) => (
              <GarageCard key={it.id} item={it} onRefresh={refresh} onDelete={remove} onEdit={startEdit} onRequestAction={startAction} />
            ))}
          </div>
        </>
      )}

      <AddDialog open={open} setOpen={setOpen} onSaved={load} editing={editing} />
      <ActionDialog open={!!actionItem} onOpenChange={(v) => !v && closeAction()} item={actionItem} actionKey={actionKey} onSent={load} />
      <SendGarageDialog open={sendOpen} onOpenChange={setSendOpen} count={items.length} onSent={load} />

      {items.length > 0 && (
        <div className="mt-12 p-6 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 flex items-start gap-4">
          <Send className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
          <div className="text-sm flex-1">
            <div className="font-medium mb-1">Ready to hand this off?</div>
            <div className="text-gray-400 text-xs mb-3">Send every vehicle in your garage to a broker in one shot. We&apos;ll route them to the right specialist and reply from a real inbox — not an auto-responder.</div>
            <button
              onClick={() => setSendOpen(true)}
              disabled={items.length === 0}
              className="text-xs px-4 py-2 rounded-full silver-gradient text-[#050507] font-bold inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid="garage-send-to-broker-btn"
            >
              <Send className="w-3.5 h-3.5" /> Send Garage to Broker ({items.length})
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
